import { Box, Divider, Skeleton, tooltipClasses, Typography } from "@mui/material";
import { StyledCard } from "./components/StyledComponents";
import CustomIcon from "../../Components/CustomIcon";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import InfoIcon from "../../Components/Icons/InfoIcon";
import { formatCompactDistanceKm, formatCompactEnergyKWh } from "../../Helper/Formatters/NumberFormatter";
import { usePagesContent } from "../../Hooks/usePagesContent";

const FleetMetricsCard = ({ stats, loading }) => {
  const {totalEnergyConsumed, totalDistanceTravelled } = stats;
  const pageContent = usePagesContent();

  // Fleet totals get large, so both step up to bigger units (kWh -> MWh -> GWh,
  // km -> k km -> M km) instead of printing every digit. Precision is org-meta
  // tunable via fleetMetrics.energyDecimals / .distanceDecimals; absent config
  // falls back to one decimal for energy and whole units for distance.
  const fleetMetricsConfig = pageContent?.metaInfo?.fleetMetrics || {};
  const totalEnergyConsumedLabel = formatCompactEnergyKWh(totalEnergyConsumed, fleetMetricsConfig.energyDecimals) ?? "-";
  const totalDistanceTravelledLabel = formatCompactDistanceKm(totalDistanceTravelled, fleetMetricsConfig.distanceDecimals) ?? "-";

  return (
    <StyledCard sx={{ boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, whiteSpace: "nowrap" }}>
          <Typography variant="fw500s16lh20c000000">Fleet Metrics</Typography>
          <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>Yesterday</Typography>
        </Box>

        {/* Side by side, each column as wide as its label (the card is content width so Live Vehicle Status
            keeps one row), centred in the height the row sets. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <CustomIcon name="charge-icon" sx={{ color: '#4D44D8', fontSize: 20 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
              <Typography variant="fw400s14c000000" sx={{ fontSize: '13px', lineHeight: '20px' }}>Energy Consumed</Typography>
              <PrimaryTooltip
                title='Total charging+regeneration energy into battery, as per "Accumulated Charging Energy"'
                sx={{
                  [`& .${tooltipClasses.tooltip}`]: {
                    maxWidth: "200px",
                    textAlign: "left !important",
                  }
                }}
              >
                <InfoIcon sx={{ width: 14, height: 14, "&:hover ": { color: "#4D44D8" }}} />
              </PrimaryTooltip>
            </Box>
            {loading ? <Skeleton variant="text" width={80} height={24} /> : <Typography variant="fw500s16lh20c000000">{totalEnergyConsumedLabel}</Typography>}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4" }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <CustomIcon name="distance-icon" sx={{ color: '#4D44D8', fontSize: 20 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
              <Typography variant="fw400s14c000000" sx={{ fontSize: '13px', lineHeight: '20px' }}>Odometer</Typography>
              <PrimaryTooltip
                title='Total distance covered as per "odometer"'
                sx={{
                  [`& .${tooltipClasses.tooltip}`]: {
                    maxWidth: "150px",
                    textAlign: "left !important",
                  }
                }}
              >
                <InfoIcon sx={{ width: 14, height: 14, "&:hover ": { color: "#4D44D8" }}} />
              </PrimaryTooltip>
            </Box>
            {loading ? <Skeleton variant="text" width={80} height={24} /> : <Typography variant="fw500s16lh20c000000">{totalDistanceTravelledLabel}</Typography>}
          </Box>
        </Box>
      </Box>
    </StyledCard>
  );
};

export default FleetMetricsCard;
